import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

const badgeStatus = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'synced' || s === 'success') return 'bg-emerald-100 text-emerald-700';
  if (s === 'pending' || s === 'processing') return 'bg-amber-100 text-amber-700';
  if (s === 'failed' || s === 'not_mapped' || s === 'max_retries') return 'bg-rose-100 text-rose-700';
  return 'bg-slate-100 text-slate-700';
};

const labelStatus = (status) => {
  const map = {
    synced: 'Sincronizado',
    pending: 'Pendente',
    processing: 'Processando',
    failed: 'Falhou',
    not_mapped: 'Não mapeado',
    max_retries: 'Máx. tentativas',
  };
  return map[status] || status || '—';
};

const RelatoriosManutencaoM8 = () => {
  const [loading, setLoading] = useState(true);
  const [itens, setItens] = useState([]);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/manutencoes/m8/status', {
        params: { somente_com_problema: true, limit: 200 },
      });
      setItens(data?.manutencoes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar falhas M8');
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const reenviar = async (id) => {
    try {
      await api.post(`/manutencoes/${id}/m8/reenviar`);
      toast.success('Integração reenfileirada');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reenviar integração');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/relatorios-manutencao" className="p-2 rounded-xl hover:bg-slate-100 text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Falhas de integração M8</h1>
            <p className="text-slate-600">Relatórios com erro para corrigir e reenviar</p>
          </div>
        </div>
        <button
          type="button"
          onClick={carregar}
          className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          Atualizar
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 text-sm text-slate-600">
          {itens.length} relatório(s) com problema
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Relatório</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Cliente</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status M8</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Erro</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-slate-500">Carregando...</td>
                </tr>
              ) : itens.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-emerald-700">
                    Nenhuma falha pendente.
                  </td>
                </tr>
              ) : (
                itens.map((m) => (
                  <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-900">
                      <div className="font-medium">{m.numero_relatorio || m.id}</div>
                      <div className="text-xs text-slate-500">OS: {m.m8_ordem_servico_id || '—'}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{m.cliente_nome || '—'}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${badgeStatus(m.m8_sync_status)}`}>
                        {labelStatus(m.m8_sync_status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-rose-700 max-w-lg">
                      <div className="line-clamp-3">{m.m8_sync_error || m.fila_ultimo_erro || '—'}</div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/relatorios-manutencao/${m.id}`}
                          className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          Ver
                        </Link>
                        <button
                          type="button"
                          onClick={() => reenviar(m.id)}
                          disabled={!m.can_retry}
                          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-40"
                        >
                          <RefreshCw className="w-4 h-4" /> Reenviar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        Se o erro for de mapeamento (cliente/técnico/contrato), corrija primeiro e depois use Reenviar.
      </div>
    </div>
  );
};

export default RelatoriosManutencaoM8;
